<template>
  <div id="app">
    <portal-target name="snackbar-destination"></portal-target>
    <portal-target name="modal-destination"></portal-target>
    <ghana-phone-callback-modal
      :visible="partnerPhoneCallbackLoading || !!partnerPhoneCallbackError"
      :loading="partnerPhoneCallbackLoading"
      :error-message="partnerPhoneCallbackError"
      :needs-pin="partnerPhoneCallbackNeedsPin"
      :signing-in-key="partnerModalSigningInKey"
      @dismiss="onPartnerPhoneCallbackDismiss"
    />
    <template v-if="setupFinished">
      <pk-header-component :logout="this.isAuthenticated" />
      <transition name="fade" mode="out-in">
        <router-view class="main_view" />
      </transition>
      <pk-footer-component />
      <download-app-banner />
    </template>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import appboy from 'appboy-web-sdk'
import i18n, { localeByCountryCode } from '@/i18n'
import sab from '@/api/sab'
import recaptchaService from '@/js/RecaptchaService'
import config from '@/config'
import faster from '@/faster'
import { getCachedIpGeoSnapshot } from '@/js/mtnGhanaGeo'
import {
  parseVenisoPartnerErrorQuery,
  resolveViuHeHashForProcessing,
  isMobileOrTabletForViuRedirect,
  decryptViuHash,
  parseViuPipePayload,
  isViuResponseSuccess,
  // isViuUserTypeEligibleForPartnerLogin,
  getViuFailureMessageI18nKey,
  loginWithViuMsisdn,
  stripViuHashFromRoute,
  buildVenisoErrorQueryFromDecryptedFailure,
  maybeRedirectViuClient,
  retryViuHeForMissingMsisdn,
  clearViuHeSessionMarkers,
  anyViuHeRegionBypassesGeo,
  getViuCarrierOverrideFromUrl,
  resolveViuCarrierFromGeoSnapshot,
  consumeViuPostLoginPath,
  resolveViuPostLoginRouterLocation
} from '@/js/viuHeaderEnrichment'

export default {
  name: 'app',
  data: () => ({
    setupFinished: false,
    partnerPhoneCallbackLoading: false,
    partnerPhoneCallbackError: null,
    partnerPhoneCallbackNeedsPin: false,
    partnerCallbackSource: null
  }),
  methods: {
    ...mapActions({
      install: 'user/install',
      verifyProfile: 'user/verifyProfile',
      refreshToken: 'authentication/refreshToken',
      establishSessionFromTokens: 'authentication/establishSessionFromTokens',
      loadContentTree: 'contentTree/load',
      trackWindowSize: 'window/track',
      signOut: 'authentication/signOut',
      hydrateMtnGhanaRegionFromIp: 'window/hydrateMtnGhanaRegionFromIp'
    }),
    ...mapMutations({
      setWebViewMode: 'window/SET_WEB_VIEW',
      setUserAgent: 'device/SET_USER_AGENT',
      setCountryCode: 'window/SET_COUNTRY_CODE',
      setLanguage: 'window/SET_LANGUAGE'
    }),
    async setupInternationalization() {
      const countryCode = await sab.getCountryCode()
      this.setCountryCode(countryCode)

      const language = localeByCountryCode[countryCode] || 'en'
      this.setLanguage(language)

      document.querySelector('html').setAttribute('lang', language)
      i18n.locale = language
    },
    onPartnerPhoneCallbackDismiss() {
      const goPhone = this.partnerPhoneCallbackNeedsPin
      this.partnerPhoneCallbackError = null
      this.partnerPhoneCallbackNeedsPin = false
      if (goPhone) {
        this.$router.push({ name: 'login', params: { type: 'phone' } }).catch(() => {})
      }
    },
    /**
     * `router.replace` can reject (e.g. aborted navigation) while Vuex is already authenticated — user
     * stays on `/login`. Full reload applies the same target URL so the next load runs guards on a clean stack.
     */
    async replaceRouteOrFullReload(location) {
      try {
        await this.$router.replace(location)
      } catch (err) {
        if (err && err.name === 'NavigationDuplicated') {
          return
        }
        try {
          const resolved = this.$router.resolve(location)
          if (resolved && resolved.href && typeof window !== 'undefined') {
            window.location.assign(resolved.href)
          }
        } catch {
          if (typeof window !== 'undefined') {
            window.location.assign('/browse')
          }
        }
      }
    },
    async finishViuHashFlow(hashParam) {
      try {
        // Runs even when a session already exists: the hash's MSISDN must win over an old session.
        const plain = await decryptViuHash(hashParam)
        const parsed = parseViuPipePayload(plain)
        const errorPath = (config.viu && config.viu.partnerErrorPath) || '/header-enrichment/error'

        // No MSISDN means the partner did not identify the subscriber. That happens under a success
        // status *and* under failure statuses — the partner's own sample for this case is
        // `|28|121|na|800004` (121 = HE failure), so this deliberately runs before the success
        // branch below. The payload's `productcode` names the carrier/market, so re-run HE against
        // that region instead of dead-ending. Falls through to the unchanged error handling below
        // when the product code is absent/unknown or the retry already fired this session.
        if (!parsed.msisdn) {
          const retry = await retryViuHeForMissingMsisdn(parsed)
          faster.emit('viu_missing_msisdn_retry', retry)
          if (retry.redirected) {
            // Navigation to the HE gateway is in flight — do not also replace to the error route.
            return
          }
        }

        if (isViuResponseSuccess(parsed)) {
          if (!parsed.msisdn) {
            clearViuHeSessionMarkers()
            await this.$router
              .replace({
                path: errorPath,
                query: buildVenisoErrorQueryFromDecryptedFailure(this.$t('auth.viuStatus.missingMsisdn'))
              })
              .catch(() => {})
            return
          }

          const outcome = await loginWithViuMsisdn(parsed.msisdn)
          if (outcome.ok) {
            clearViuHeSessionMarkers()
            if (!this.isAuthenticated) {
              await this.establishSessionFromTokens()
            }
            // Browse requires `contentTree.isLoaded`; guards await it — load must finish before `replace`.
            await this.loadContentTree(this.countryCode)
            // Ensures `authorizationGuard` sees `isProfileLoaded` (finalize can throw after tokens; do not skip).
            await this.verifyProfile()
            const postLogin = resolveViuPostLoginRouterLocation(
              consumeViuPostLoginPath(),
              this.$route
            )
            // Hide modal before navigation — `replace` can wait on guards; UI was stuck on /login otherwise.
            this.partnerPhoneCallbackLoading = false
            await this.$nextTick()
            await this.replaceRouteOrFullReload(postLogin)
            return
          }
          if (outcome.invalidMsisdn) {
            this.partnerPhoneCallbackError =
              outcome.serverErrorMessage || this.$t('auth.ghanaCallback.invalidMsisdn')
            clearViuHeSessionMarkers()
            stripViuHashFromRoute(this.$router, this.$route.query)
            return
          }
          if (outcome.subscriptionInactive) {
            this.partnerPhoneCallbackError =
              outcome.serverErrorMessage || this.$t('auth.viuStatus.noActiveSubscription')
            clearViuHeSessionMarkers()
            stripViuHashFromRoute(this.$router, this.$route.query)
            return
          }
          if (outcome.subscriptionCheckFailed) {
            this.partnerPhoneCallbackError =
              outcome.serverErrorMessage || this.$t('auth.ghanaCallback.genericError')
            clearViuHeSessionMarkers()
            stripViuHashFromRoute(this.$router, this.$route.query)
            return
          }
          clearViuHeSessionMarkers()
          this.partnerPhoneCallbackError =
            outcome.serverErrorMessage || this.$t('auth.ghanaCallback.genericError')
          stripViuHashFromRoute(this.$router, this.$route.query)
          return
        }

        const desc = this.$t('auth.viuStatus.' + getViuFailureMessageI18nKey(parsed))
        clearViuHeSessionMarkers()
        await this.$router
          .replace({
            path: errorPath,
            query: buildVenisoErrorQueryFromDecryptedFailure(desc)
          })
          .catch(() => {})
      } catch {
        const errorPath = (config.viu && config.viu.partnerErrorPath) || '/header-enrichment/error'
        stripViuHashFromRoute(this.$router, this.$route.query)
        clearViuHeSessionMarkers()

        // An inbound hash that fails to decrypt/parse falls back to the HE gateway redirect instead
        // of failing outright — same recovery `maybeRedirectViuClient` already gives a *missing* hash
        // in `created()`. This applies uniformly across VIU carriers (no per-telco branching): the
        // fallback is "redo the HE gateway round trip," which is carrier-agnostic by construction.
        // Kill switch: VUE_APP_VIU_HASH_DECODE_FALLBACK_ENABLED (defaults on).
        const fallbackEnabled = !config.viu || config.viu.hashDecodeFallbackEnabled !== false
        let fallbackTriggered = false
        if (fallbackEnabled) {
          try {
            fallbackTriggered = await maybeRedirectViuClient()
          } catch {
            fallbackTriggered = false
          }
        }

        const geo = getCachedIpGeoSnapshot()
        faster.emit('viu_hash_decode_fallback', {
          countryCode: (geo && geo.countryCode) || '',
          carrier: getViuCarrierOverrideFromUrl() || resolveViuCarrierFromGeoSnapshot(geo) || '',
          fallbackTriggered
        })

        if (fallbackTriggered) {
          // Navigation to the HE gateway is already in flight — do not also replace to the error route.
          return
        }

        await this.$router
          .replace({
            path: errorPath,
            query: buildVenisoErrorQueryFromDecryptedFailure(this.$t('auth.viuCallback.somethingWentWrong'))
          })
          .catch(() => {})
      }
    },
    async authenticateUser() {
      await this.install()
      await this.refreshToken()
      if (window.location.pathname === '/login/reset') {
        await this.signOut()
        this.$router.replace({
          path: window.location.pathname + window.location.search
        })
        return
      }
      if (this.isAuthenticated) {
        await this.verifyProfile()
        appboy.changeUser(this.accountId)
        appboy.openSession()
      }
    }
  },
  computed: {
    ...mapState({
      isStoreLoaded: state => state.contentTree.isLoaded,
      isAuthenticated: state => state.authentication.authenticated,
      countryCode: state => state.window.countryCode,
      accountId: state => state.user.accountId
    }),
    partnerModalSigningInKey() {
      return this.partnerCallbackSource === 'viu'
        ? 'auth.viuCallback.signingIn'
        : 'auth.ghanaCallback.signingIn'
    }
  },
  async created() {
    window.dataLayer.push({ event: 'optimize.activate' })
    setTimeout(() => {
      window.focus()
    }, 2000)

    this.setWebViewMode(process.env.VUE_APP_CONFIG_ENVIRONMENT.includes('lite'))
    this.setUserAgent(window.navigator.userAgent)
    this.trackWindowSize()

    recaptchaService.init()
    window.remoteConfig.settings.minimumFetchIntervalMillis = 0

    const venisoError = parseVenisoPartnerErrorQuery(this.$route.query)
    // HE return `?hash=` is handled only on phone/tablet (same rule as maybeRedirectViuClient).
    const rawViuHash =
      config.viu &&
      config.viu.enabled &&
      !venisoError &&
      resolveViuHeHashForProcessing(this.$route.query)
    const viuHeDeviceAllowed =
      isMobileOrTabletForViuRedirect(anyViuHeRegionBypassesGeo()) ||
      Boolean(config.viu && config.viu.allowDesktopHeCallback)
    if (rawViuHash && !viuHeDeviceAllowed) {
      stripViuHashFromRoute(this.$router, this.$route.query)
      clearViuHeSessionMarkers()
    }
    const viuHash = rawViuHash && viuHeDeviceAllowed ? rawViuHash : null

    if (viuHash) {
      this.partnerCallbackSource = 'viu'
      this.partnerPhoneCallbackLoading = true
    }

    // Restore session before deciding outbound HE redirect (skip logged-in users).
    await this.install()
    const hasStoredSessionTokens = Boolean(
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('accessToken') &&
      localStorage.getItem('refreshToken')
    )

    if (!venisoError && !viuHash && !this.isAuthenticated && !hasStoredSessionTokens) {
      // Run before Remote Config / i18n / duplicate geo hydrate so the browser leaves for Viu quickly.
      await maybeRedirectViuClient()
    }

    // i18n + country are needed for first paint; Remote Config and MTN IP geo are not blocking.
    await this.setupInternationalization()
    void window.remoteConfig.fetchAndActivate().catch(() => {})
    void this.hydrateMtnGhanaRegionFromIp().catch(() => {})

    // Show shell while session refresh runs — avoids long blank screen on slow mobile networks.
    this.setupFinished = true

    await this.authenticateUser()

    const errorPath = (config.viu && config.viu.partnerErrorPath) || '/header-enrichment/error'
    if (venisoError) {
      if (this.$route.path !== errorPath) {
        await this.$router.replace({ path: errorPath, query: this.$route.query }).catch(() => {})
      }
      this.loadContentTree(this.countryCode)
      return
    }

    await this.loadContentTree(this.countryCode)

    if (viuHash) {
      // Always log in with the hash, even over an existing session: the old session may belong to
      // another user (e.g. an earlier test on the same device) and would show their subscription.
      try {
        await this.finishViuHashFlow(viuHash)
        // Do not call full `authenticateUser()` here: `refreshToken` can fail transiently right after
        // VIU login and clear `authenticated`, leaving tokens in storage but the UI on `/login`.
        // Session + profile are established inside `loginWithViuMsisdn` / `finishViuHashFlow`.
      } finally {
        this.partnerPhoneCallbackLoading = false
      }
    } else {
      this.partnerPhoneCallbackLoading = false
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/main';

*, *::before, *::after {
  box-sizing: border-box;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  -khtml-user-select: none;
  user-select: none;
}

input, input:before, input:after, a {
  -khtml-user-select: initial;
  user-select: initial;
}

html {
  color: #453D4C;
  font-size: 14px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
}

#app {
  // overflow-x: hidden;
  min-height: 100vh;
  font-family: 'Nunito', 'Avenir', Helvetica, Arial, sans-serif;
  // font-weight: 900;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  max-width: 1400px;
  margin: auto;
  box-shadow: 0 0 16px rgba(0,0,0,0.2);
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.main_view {
  flex-grow: 1;
  margin-top: 64px;
}

button, h1, h2, h3 {
  font-family: 'Nunito', 'Avenir', Helvetica, Arial, sans-serif;
  font-weight: 900;
}

a {
  font-weight: bold;
  color: #A217AB;
  text-decoration: underline;
  cursor: pointer;

  &:visited {
    color: #A217AB;
  }
}
</style>
